import { span } from '$/lib/sentry.decorators'
import { building, dev } from '$app/environment'
import {
  type AuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  type IdTokenResult,
  TwitterAuthProvider,
  type User,
  type UserCredential,
  getIdTokenResult,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithCustomToken,
  signInWithEmailLink,
  signInWithPopup,
} from 'firebase/auth'
import { derived, get, writable } from 'svelte/store'
import { getFirebaseAuthUser, initFirebaseAuth } from './firebase'

export type AuthenticatedUser = User & { roles: string[] }
export type SupportedSocialAuth =
  | 'facebook'
  | 'google'
  | 'apple'
  | 'twitter'
  | 'metamask'

class AuthStore {
  private userData = writable<User | null>(null)
  private tokenResultData = writable<IdTokenResult | null>(null)
  private userRoles = derived(this.tokenResultData, (t): string[] => {
    const roles = t?.claims.roles
    if (Array.isArray(roles)) return roles
    if (typeof roles === 'string') return [roles]
    return []
  })

  /** True if the store is checking for auth state */
  public isChecking = writable(false)
  /** The authenticated user from Firebase Auth */
  public authenticatedUser = derived(
    [this.userData, this.userRoles],
    ([u, r]): AuthenticatedUser | null => {
      if (u) (u as AuthenticatedUser).roles = r
      return u as AuthenticatedUser
    }
  )
  public waitForInit: Promise<User | null>
  private _resolveInit: (u: User | null) => void = () => {}

  constructor() {
    this.waitForInit = new Promise(resolve => {
      this._resolveInit = resolve
    })
    if (building) this._resolveInit(null)
  }

  @span()
  async init(): Promise<void> {
    if (building) return
    let user: User | null = null
    this.isChecking.set(true)
    try {
      const auth = await initFirebaseAuth()
      user = await getFirebaseAuthUser(auth)
      if (user) {
        const tokenResult = await getIdTokenResult(user, false)
        this.userData.set(user)
        this.tokenResultData.set(tokenResult)
      } else {
        this.userData.set(null)
        this.tokenResultData.set(null)
      }
    } finally {
      this.isChecking.set(false)
      this._resolveInit(user)
    }
  }

  @span()
  async signInWithSocial(name: SupportedSocialAuth) {
    try {
      // if (name === 'metamask') return this._signinWithMetamask()

      const auth = await initFirebaseAuth()
      auth.useDeviceLanguage()
      const provider: AuthProvider | null =
        name === 'facebook'
          ? new FacebookAuthProvider()
          : name === 'google'
            ? new GoogleAuthProvider()
            : name === 'twitter'
              ? new TwitterAuthProvider()
              : null
      if (!provider) throw new Error('Unsupported provider!')

      const result = await signInWithPopup(auth, provider)
      await this._handleAuthResult(result)
    } catch (e) {
      if (dev) console.log('🚫 Login failed', e)
      throw e
    }
  }
  // private async _signinWithMetamask() {
  //   try {
  //     const client = this.getTrpcClient()
  //     let provider: BrowserProvider | undefined
  //     if (window.ethereum) provider = new BrowserProvider(window.ethereum)
  //     if (!provider) throw new Error('MetaMask extension not found!')

  //     await provider.send('eth_requestAccounts', []) // Request browser to connect wallet
  //     const signer = await provider.getSigner()
  //     const message = await client.siweInit.mutate({ address: signer.address })
  //     if (!message) throw new Error('Cannot initialize sign in message, please try again!')
  //     const signature = await signer.signMessage(message)
  //     const customToken = await client.siweVerify.mutate({ message, signature })
  //     await authStore.signInWithToken(customToken)
  //   } catch (e) {
  //     if (dev) console.log('❗️ signinWithMetamask Error', e)
  //     if (e instanceof Error) {
  //       if (isError(e, 'ACTION_REJECTED')) throw new Error('Sign in request rejected!')
  //       throw e
  //     }
  //   }
  // }

  async sendSignInLinkToEmail(email: string, callbackUrl: string) {
    const auth = await initFirebaseAuth()
    await sendSignInLinkToEmail(auth, email, {
      url: callbackUrl,
      handleCodeInApp: true,
    })
  }
  async signInWithEmailLink(email: string, link: string) {
    try {
      const auth = await initFirebaseAuth()
      const result = await signInWithEmailLink(auth, email, link)
      await this._handleAuthResult(result)
    } catch (e) {
      console.log('🚫 Login failed', e)
      throw e
    }
  }
  async signInWithToken(customToken: string) {
    try {
      const auth = await initFirebaseAuth()
      const result = await signInWithCustomToken(auth, customToken)
      await this._handleAuthResult(result)
    } catch (e) {
      console.log('🚫 Login failed', e)
      throw e
    }
  }
  async signOut() {
    const auth = await initFirebaseAuth()
    await auth.signOut()
    this.userData.set(null)
    this.tokenResultData.set(null)
  }
  async isSignInWithEmailLink(link: string) {
    const auth = await initFirebaseAuth()
    return isSignInWithEmailLink(auth, link)
  }
  async getIdToken(forceRefresh?: boolean) {
    return await get(this.userData)?.getIdToken(forceRefresh)
  }
  async getAuthHeader(
    forceRefresh?: boolean
  ): Promise<{ Authorization?: string }> {
    await this.waitForInit
    const token = await this.getIdToken(forceRefresh)
    if (token) return { Authorization: `Bearer ${token}` }
    return {}
  }

  private async _handleAuthResult(result: UserCredential) {
    const tokenResult = await getIdTokenResult(result.user, false)

    console.log('✅ Login succeeded!', result)
    console.log('✅ tokenResult', tokenResult)

    this.userData.set(result.user)
    this.tokenResultData.set(tokenResult)
  }
}

export const authStore = new AuthStore()
export const isChecking = authStore.isChecking
export const authenticatedUser = authStore.authenticatedUser
